import type { ReactNode } from 'react';

import { Icon, type IconName } from '@/components/ui/Icon';
import { cn } from '@/utils/cn';

import styles from './Tabs.module.css';

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** A number after the label, e.g. how many rows the tab holds. */
  count?: number | undefined;
  icon?: IconName;
}

interface ChoiceProps<T extends string> {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the group for screen readers. */
  label: string;
  className?: string | undefined;
}

function Count({ value }: { value: number | undefined }) {
  if (value === undefined) return null;
  return <span className={cn(styles.count, 'tabular')}>{value.toLocaleString('en-IN')}</span>;
}

/** Tabs switch between views of the same thing (underline style). */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: ChoiceProps<T>) {
  return (
    <div role="tablist" aria-label={label} className={cn(styles.tabs, className)}>
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={selected}
            className={cn(styles.tab, selected && styles.tabSelected)}
            onClick={() => {
              onChange(item.value);
            }}
          >
            {item.icon && <Icon name={item.icon} size={18} />}
            {item.label}
            <Count value={item.count} />
          </button>
        );
      })}
    </div>
  );
}

/** A segmented control switches a mode or range (pill group). */
export function SegmentedControl<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: ChoiceProps<T>) {
  return (
    <div role="group" aria-label={label} className={cn(styles.segments, className)}>
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            aria-pressed={selected}
            className={cn(styles.segment, selected && styles.segmentSelected)}
            onClick={() => {
              onChange(item.value);
            }}
          >
            {item.icon && <Icon name={item.icon} size={18} />}
            {item.label}
            <Count value={item.count} />
          </button>
        );
      })}
    </div>
  );
}

/** Chips filter a list; the count shows how many rows each filter holds. */
export function FilterChips<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: ChoiceProps<T>) {
  return (
    <div role="group" aria-label={label} className={cn(styles.chips, className)}>
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            aria-pressed={selected}
            className={cn(styles.chip, selected && styles.chipSelected)}
            onClick={() => {
              onChange(item.value);
            }}
          >
            {item.label}
            <Count value={item.count} />
          </button>
        );
      })}
    </div>
  );
}
